/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Formik } from "formik";
import { GlobalLoaderContext, GlobalAuthContext } from "store/context";
import Layout from "components/Layout";
import ContentCard from "components/ContentCard";
import Loader from "components/Loader";
import Row from "components/Grid/Row";
import FormContainer from "components/FormContainer";
import Button, { ButtonContainer } from 'components/Button';
import Colors from "constants/Colors";
import { FitinsurAPI, MessageCenter } from "utils";
import {
  ButtonContent,
  FieldText,
  DropzoneContainer,
  UploadMessage
} from "./styles";
import ImportPreview from './ImportPreview';
import Dropzone from 'react-dropzone';
import * as XLSX from 'xlsx';
import { intersection, orderBy } from 'lodash';
import moment from 'moment';
import Access from 'constants/Access';

const defaultColumnsFile = [
  'ACRONYM_CURRENCY',
  'YEAR',
  'MONTH',
  'EX_RATE'
].sort();

export default function ImportCurrencyRate() {
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const history = useHistory();
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();

  const [validation, setValidation] = useState(false);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [updatedFileData, setUpdatedFileData] = useState(null)
  const [hasPermission, setHasPermission] = useState(true);

  useEffect(() => {
    permissionAccess();
  }, []);

  const renderDragMessage = (isDragActive, isDragReject) => {
    if (!isDragActive)
      return <UploadMessage>Drag and Drop Documents here to upload</UploadMessage>;

    if (isDragReject)
      return <UploadMessage type="error">Unsupported file</UploadMessage>;

    return <UploadMessage type="success">Drop file here</UploadMessage>;
  };

  const handleFileImported = async (file) => {
    let currencyRates = []

    const workBook = XLSX.read(file, { cellDates: true, dateNF: 'mm/dd/yyyy;@' });

    workBook.SheetNames.map(sheetName => {
      currencyRates = currencyRates.concat(XLSX.utils.sheet_to_json(workBook.Sheets[sheetName], { defval: null, raw: false }));
    });

    currencyRates = currencyRates.filter(rate => {
      const columns = Object.keys(rate).map(v => v.trim()).sort();
      return JSON.stringify(defaultColumnsFile) === JSON.stringify(intersection(defaultColumnsFile, columns));
    });

    if (!currencyRates.length)
      return mc.error('The file is not in the required standard');

    function validateMonth(month) {
      return (month.length === 1 || month.length === 2) &&
        (Number(month) >= 1 && Number(month) <= 12);
    }

    const formattedCurrencyRates = currencyRates.map(rate => {
      let validateMonthAndYear = true;
      if (!(rate.ACRONYM_CURRENCY && rate.ACRONYM_CURRENCY.length === 3)) {
        rate.error = `
          Column value ACRONYM_CURRENCY invalid. 
          (Line ${Number(rate.__rowNum__) + 1} of the table)
        `
      } else if (!(rate.YEAR && rate.YEAR.length === 4 && !!Number(rate.YEAR))) {
        rate.error = `
          Column value YEAR invalid. 
          (Line ${Number(rate.__rowNum__) + 1} of the table)
        `
        validateMonthAndYear = false;
      } else if (!(rate.MONTH && !!Number(rate.MONTH) && validateMonth(rate.MONTH))) {
        rate.error = `
          Column value MONTH invalid. 
          (Line ${Number(rate.__rowNum__) + 1} of the table)
        `
        validateMonthAndYear = false;
      } else if (!(rate.EX_RATE && !!Number(rate.EX_RATE.replace(',', '.')))) {
        rate.error = `
          Column value EX_RATE invalid. 
          (Line ${Number(rate.__rowNum__) + 1} of the table)
        `
      }

      const formattedDate = validateMonthAndYear ?
        moment().set({
          year: rate.YEAR,
          month: rate.MONTH - 1,
          date: 1,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0
        }).format() : null;

      return {
        currencyCode: rate.ACRONYM_CURRENCY.trim().toUpperCase(),
        currencyRate: Number(rate.EX_RATE ? rate.EX_RATE.replace(',', '.') : ''),
        date: formattedDate,
        year: rate.YEAR,
        month: rate.MONTH,
        error: rate.error ? rate.error : 'OK'
      }
    });

    const currencyRatesOrdering = orderBy(formattedCurrencyRates, ['date', 'currencyCode'], ['asc', 'asc']);

    const errorsValidation = formattedCurrencyRates.find(rate => rate.error !== 'OK');

    if (!errorsValidation)
      setValidation(true);

    setCurrencyRates(currencyRatesOrdering);
  }

  const handleImport = async (files) => {
    loading.start();
    await handleUploadFile(files[0]);
    const file = await files[0].arrayBuffer();
    await handleFileImported(file);
    loading.stop();
  }

  const handleUploadFile = async (paramFile) => {
    const formData = new FormData();

    formData.append('files', paramFile);
    formData.append('idOperation', "");
    formData.append('idTypeDocument', "");
    formData.append('dsTypeDocument', "");
    formData.append('isDraft', "");
    formData.append('isInternalDocument', "");

    fitinsur.setAuthToken(authContext.token);
    const response = await fitinsur.requestFileUpload(formData).then(response => response.json());
    if (response.ret_code !== 200) {
      loading.stop();
      mc.error(response.ret_msg_details);
      return;
    }

    const [file] = response.ret_value;

    setUpdatedFileData(file)
  }

  const handleRejectEvent = event => {
    if (event.length > 1)
      return mc.error('Only one file can be imported at a time');

    if (event[0].errors[0].code === 'file-invalid-type')
      return mc.error('Invalid format file');
  }

  const importCurrencyRates = async () => {
    loading.start();
    try {
      fitinsur.setAuthToken(authContext.token);
      console.log(currencyRates)
      const response = await fitinsur.importCurrencyRates(
        currencyRates,
        updatedFileData.documentId
      ).then(res => res.json());

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setCurrencyRates([]);

      checkImport(updatedFileData.documentId);
    } catch (err) {
      loading.stop();
      mc.error("Sorry, there was an error uploading your file, please try again!");
    };
  }

  const checkImport = async (documentId) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestProcessReportFileStatus(
        documentId).then(response => response.json()
        );

      if (
        response.ret_code !== 200 ||
        (response.ret_value.processing && response.ret_value.readyToProcess)
      ) {
        return setTimeout(() => checkImport(documentId), 10000);
      }

      setUpdatedFileData(null)
      loading.stop();
      mc.success('Your file was processed successfully!');
    } catch (e) {
      mc.error('Sorry, there was an error uploading your file, please try again!');
      loading.stop();
    }
  };

  async function permissionAccess() {
    try {
      const response = await fitinsur
        .requestAccess("", "CREATE", Access.PERSON_EDIT, null, null, authContext.token)
        .then((response) => response.json())
      const hasPermission = response && response.ret_code === 200;
      setHasPermission(hasPermission);
    } catch (e) {
      setHasPermission(false);
    }
  }

  return (
    <Formik>
      <Layout title={'CURRENCY RATE'}>
        <ContentCard title="IMPORT CURRENCY RATE">
          <Dropzone
            onDropAccepted={handleImport}
            onDropRejected={handleRejectEvent}
            maxFiles={1}
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">
            {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
              <DropzoneContainer
                {...getRootProps()}
                isDragActive={isDragActive}
                isDragReject={isDragReject}
              >
                <input {...getInputProps()} />
                {renderDragMessage(isDragActive, isDragReject)}
              </DropzoneContainer>
            )}
          </Dropzone>
          <Row>
            <FieldText size={12}>
              Example file for:
              <a
                style={{ textDecoration: 'none' }}
                href="https://integration.fitinsur.com.br:30443/fitinsur/app/service/staticfile?filename=model_import_currency_rate.xlsx"
              >
                &nbsp;XLSX
              </a>
            </FieldText>
          </Row>
          {currencyRates.length ?
            (
              <FormContainer title="Import Preview">
                <ImportPreview file={currencyRates} />
              </FormContainer>
            ) : ''
          }
          <ButtonContainer>
            <Button
              forth
              onClick={() => history.goBack()}
              style={{ alignSelf: 'flex-start' }}
            >
              &lt; Back
            </Button>
            <ButtonContent>
              {validation &&
                <Button
                  type="submit"
                  primary={true}
                  onClick={() => importCurrencyRates()}
                  disabled={!hasPermission}
                >
                  IMPORT
                </Button>
              }
            </ButtonContent>
          </ButtonContainer>
          {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
        </ContentCard>
      </Layout>
    </Formik>
  );
};
